// Presentation metadata for informational-event kinds — labels and mdi icons
// used when rendering filter chips and cards on the `/discover` surface.
//
// No Vue/Nuxt imports — auto-imported by Nuxt from `app/utils/`.

export type InformationalEventKind =
  | 'rally'
  | 'convention'
  | 'expo'
  | 'festival'
  | 'gathering';

export interface InformationalEventKindMeta {
  label: string;
  icon: string;
}

// Ordered for stable chip rendering — controls the display order of filters.
export const INFORMATIONAL_EVENT_KINDS: readonly InformationalEventKind[] = [
  'rally',
  'convention',
  'expo',
  'festival',
  'gathering',
] as const;

export const INFORMATIONAL_EVENT_KIND_META: Record<
  InformationalEventKind,
  InformationalEventKindMeta
> = {
  rally: { label: 'Rally', icon: 'mdi-account-group' },
  convention: { label: 'Convention', icon: 'mdi-podium' },
  expo: { label: 'Expo', icon: 'mdi-storefront' },
  festival: { label: 'Festival', icon: 'mdi-party-popper' },
  gathering: { label: 'Gathering', icon: 'mdi-campfire' },
};

// Fallback for unknown/unmapped kinds (the backend `event_kind` is a plain
// string, so new kinds may arrive before this map is updated). Capitalizes the
// raw kind for the label and uses a generic calendar icon.
function fallbackMeta(kind: string): InformationalEventKindMeta {
  const label = kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : 'Event';
  return { label, icon: 'mdi-calendar-blank' };
}

/**
 * Look up presentation metadata for an event kind, falling back to a
 * capitalized label + generic icon for kinds not in the known map.
 */
export function informationalEventKindMeta(
  kind: string,
): InformationalEventKindMeta {
  return (
    INFORMATIONAL_EVENT_KIND_META[kind as InformationalEventKind] ??
    fallbackMeta(kind)
  );
}
